/**
 * ============================================================
 * GLOBAL COUNTDOWN TIMER — Proyecto Millonarios FC 2026
 * ============================================================
 * Sistema de campaña global: UNA configuración activa
 * se refleja en toda la tienda automáticamente.
 *
 * Flujo:
 * 1. Liquid renderiza los datos de campaña en el DOM
 * 2. JS los lee y arranca el timer
 * 3. Gestiona estados: upcoming → active → expired
 * 4. Se auto-destruye al expirar (opcional)
 * ============================================================
 */

window.GlobalCountdown = (function () {
  'use strict';

  // ── Constantes ─────────────────────────────────────────────
  const NS          = 'GlobalCountdown';
  const TICK_MS     = 1000;
  const DATA_KEY    = 'data-gct';
  const STORAGE_KEY = 'gct_float_dismissed';

  // ── Estado interno ─────────────────────────────────────────
  let _intervalId   = null;
  let _config       = null;
  let _elements     = [];
  let _state        = 'idle'; // idle | upcoming | active | expired

  // ── Utilidades ─────────────────────────────────────────────
  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

  function parseISODate(str) {
    if (!str || str === 'nil' || str === '') return null;
    const ts = Date.parse(str);
    return isNaN(ts) ? null : ts;
  }

  function getNow() {
    return Date.now();
  }

  function msToUnits(ms) {
    if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const s = Math.floor(ms / 1000);
    return {
      days    : Math.floor(s / 86400),
      hours   : Math.floor((s % 86400) / 3600),
      minutes : Math.floor((s % 3600) / 60),
      seconds : s % 60,
    };
  }

  function isDismissed(campaignKey) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return stored[campaignKey] === true;
    } catch {
      return false;
    }
  }

  function setDismissed(campaignKey) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      stored[campaignKey] = true;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {}
  }

  // ── Leer config desde el DOM ────────────────────────────────
  function readConfig() {
    const el = document.getElementById('gct-config');
    if (!el) return null;

    const d = el.dataset;

    return {
      campaignKey    : d.gctKey      || 'default',
      campaignName   : d.gctName     || 'Campaña',
      active         : d.gctActive   === 'true',
      startDate      : parseISODate(d.gctStart),
      endDate        : parseISODate(d.gctEnd),
      title          : d.gctTitle    || 'Oferta por tiempo limitado',
      subtitle       : d.gctSubtitle || '',
      theme          : d.gctTheme    || 'dark',
      position       : d.gctPosition || 'bar',   // bar | inline | float | all
      showPages      : d.gctPages    || 'all',    // all | home | product | collection | cart
      urgencyHours   : parseInt(d.gctUrgency || '2', 10),
      hideOnExpiry   : d.gctHideExp  === 'true',
      ctaText        : d.gctCtaText  || '',
      ctaUrl         : d.gctCtaUrl   || '',
      labelDays      : d.gctLDays    || 'Días',
      labelHours     : d.gctLHours   || 'Horas',
      labelMinutes   : d.gctLMin     || 'Min',
      labelSeconds   : d.gctLSec     || 'Seg'
    };
  }

  // ── Detectar página actual ──────────────────────────────────
  function detectPageType() {
    const main = document.querySelector('main');
    const template = main?.dataset.template;

    const templateMap = {
        index:      'home',
        product:    'product',
        collection: 'collection',
        cart:       'cart',
        page:       'page',
        blog:       'blog'
    };

    return templateMap[template] || 'other';
  }

  function shouldShowOnPage(showPages) {
    if (!showPages || showPages === 'all') return true;
    const current  = detectPageType();
    const allowed  = showPages.split(',').map(p => p.trim());
    return allowed.includes('all') || allowed.includes(current);
  }

  // ── Encontrar todos los containers en el DOM ────────────────
  function findContainers() {
    return Array.from(document.querySelectorAll('[data-gct-container]'));
  }

  // ── Actualizar número con animación flip ────────────────────
  function updateNumber(el, newVal) {
    if (!el) return;
    const str = pad(newVal);
    if (el.textContent === str) return;

    el.classList.add('gct-flip');
    setTimeout(() => {
      el.textContent = str;
      el.classList.remove('gct-flip');
    }, 80);
  }

  // ── Actualizar todos los timers en el DOM ───────────────────
  function updateAllTimers(units) {
    _elements.forEach(container => {
      const q = (sel) => container.querySelector(sel);
      updateNumber(q('[data-gct-days]'),    units.days);
      updateNumber(q('[data-gct-hours]'),   units.hours);
      updateNumber(q('[data-gct-minutes]'), units.minutes);
      updateNumber(q('[data-gct-seconds]'), units.seconds);
    });
  }

  // ── Mostrar containers ──────────────────────────────────────
  function showContainers() {
    _elements.forEach(el => {
      el.classList.add('gct-visible');
    });
  }

  function hideContainers() {
    _elements.forEach(el => {
      el.classList.remove('gct-visible');
    });
  }

  // ── Aplicar clase de estado ─────────────────────────────────
  function applyStateClass(state) {
    _elements.forEach(el => {
      el.classList.remove('gct-upcoming', 'gct-active', 'gct-expired', 'gct-urgent');
      el.classList.add(`gct-${state}`);
    });
  }

  // ── Actualizar títulos ──────────────────────────────────────
  function updateTitles(text) {
    document.querySelectorAll('[data-gct-title]').forEach(el => {
      el.textContent = text;
    });
  }

  // ── TICK principal ──────────────────────────────────────────
  function tick() {
    const now = getNow();

    console.log(now);
    const { startDate, endDate, urgencyHours } = _config;
    const urgencyMs = urgencyHours * 60 * 60 * 1000;

    // ── ANTES DEL START ──────────────────────────────────────
    if (startDate && now < startDate) {

      if (_state !== 'upcoming') {
        _state = 'upcoming';
        applyStateClass('upcoming');
        hideContainers();
        updateTitles(_config.subtitle || 'Pronto disponible');
        console.info(`[${NS}] Estado: upcoming`);
      }

      const diff  = startDate - now;
      updateAllTimers(msToUnits(diff));
      return;
    }

    // ── EXPIRADO ─────────────────────────────────────────────
    if (now >= endDate) {

      if (_state !== 'expired') {
        _state = 'expired';
        stop();
        applyStateClass('expired');

        updateAllTimers({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        updateTitles('¡La oferta ha terminado!');

        if (_config.hideOnExpiry) {
          setTimeout(hideContainers, 2000);
        }

        // Disparar evento global para otros scripts
        document.dispatchEvent(new CustomEvent('gct:expired', {
          detail: { campaignKey: _config.campaignKey }
        }));

        console.info(`[${NS}] Estado: expired`);
      }
      return;
    }

    // ── ACTIVO ────────────────────────────────────────────────
    if (_state !== 'active') {
      _state = 'active';
      applyStateClass('active');
      showContainers();
      updateTitles(_config.title);

      // Evento global
      document.dispatchEvent(new CustomEvent('gct:started', {
        detail: { campaignKey: _config.campaignKey }
      }));

      console.info(`[${NS}] Estado: active — Campaign: ${_config.campaignKey}`);
    }

    const remaining = endDate - now;
    updateAllTimers(msToUnits(remaining));

    // Urgency
    if (remaining <= urgencyMs) {
      _elements.forEach(el => el.classList.add('gct-urgent'));
    }
  }

  // ── Start / Stop ────────────────────────────────────────────
  function start() {
    if (_intervalId) return;
    tick();
    _intervalId = setInterval(tick, TICK_MS);
  }

  function stop() {
    clearInterval(_intervalId);
    _intervalId = null;
  }

  // ── Configurar botón de cierre (float) ──────────────────────
  function setupCloseButtons() {
    document.querySelectorAll('[data-gct-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        const floatEl = btn.closest('[data-gct-container]');
        if (floatEl) {
          floatEl.classList.remove('gct-visible');
          floatEl.style.display = 'none';
          setDismissed(_config.campaignKey);
        }
      });
    });
  }

  // ── Init principal ──────────────────────────────────────────
  function init() {
    _config = readConfig();

    if (!_config) {
      // Sin config en el DOM → no hay campaña
      return;
    }

    if (!_config.active) {
      console.info(`[${NS}] Campaña inactiva.`);
      return;
    }

    if (!_config.endDate) {
      console.warn(`[${NS}] endDate requerido.`);
      return;
    }

    if (!shouldShowOnPage(_config.showPages)) {
      console.info(`[${NS}] Timer no configurado para esta página (${detectPageType()}).`);
      return;
    }

    // Obtener todos los containers
    _elements = findContainers();

    if (!_elements.length) {
      console.warn(`[${NS}] No se encontraron containers [data-gct-container].`);
      return;
    }

    // Ocultar float si fue cerrado en esta sesión
    if (_config.position === 'float' || _config.position === 'all') {
      if (isDismissed(_config.campaignKey)) {
        document.querySelectorAll('[data-gct-container="float"]').forEach(el => {
          el.style.display = 'none';
        });
      }
    }

    setupCloseButtons();
    start();
  }

  // ── DOM Ready ───────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor hot-reload
  document.addEventListener('shopify:section:load',   init);
  document.addEventListener('shopify:section:unload', stop);

  // ── API pública ─────────────────────────────────────────────
  return {
    getState  : () => _state,
    getConfig : () => _config,
    stop,
    restart   : () => { stop(); _state = 'idle'; init(); },
  };

}());